<template>
  <div>
    <!-- KPI row — same card design language as the Lives detail
         view. Streams / Total Unique Users / Playback Starts /
         Avg Duration / Watch Time. Each card carries an icon
         tile so the metric reads at a glance. -->
    <div class="kpi-grid mb-6">
      <template v-if="panelLoading.kpis && !data?.sessions">
        <div v-for="i in 6" :key="'skel-stat-' + i" class="bg-white rounded-xl border border-gray-200/60 p-5">
          <LoadingBlock height="80" label="Loading stats..." />
        </div>
      </template>
      <template v-else-if="data?.sessions">
        <div
          v-for="(card, i) in kpiCards"
          :key="card.key"
          :class="[
            'animate-fade-in relative',
            `stagger-${i + 1}`,
            activeInfoCard === (card.key || card.label) ? 'z-50' : ''
          ]"
        >
          <div class="bg-white rounded-xl border border-gray-200/60 p-5">
            <div class="flex items-center justify-between mb-3">
              <div class="flex items-center gap-1.5">
                <span class="text-sm font-medium text-gray-400 uppercase tracking-wider">{{ card.label }}</span>
                <div v-if="card.info" class="relative">
                  <button
                    @click.stop="toggleInfo(card.key || card.label)"
                    type="button"
                    class="w-4 h-4 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 flex items-center justify-center text-[10px] font-semibold leading-none transition-colors"
                    :title="card.info"
                  >?</button>
                  <!-- Info popover — anchored to the `?` button, opens
                       just below it. The last card in the row anchors
                       to the right instead so it grows leftward and
                       doesn't push horizontal scroll. -->
                  <div
                    v-if="activeInfoCard === (card.key || card.label)"
                    @click.stop
                    :class="[
                      'absolute top-full mt-2 z-50 w-64 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm text-gray-600 leading-relaxed',
                      i === kpiCards.length - 1 ? 'right-0' : 'left-0'
                    ]"
                  >{{ card.info }}</div>
                </div>
              </div>
              <span :class="['flex items-center justify-center w-9 h-9 rounded-lg', card.iconBg]" v-html="card.icon" />
            </div>

            <div class="flex items-baseline gap-2">
              <div class="font-data text-[26px] font-medium text-gray-900 leading-none">{{ card.value }}</div>
              <DeltaBadge v-if="card.current != null" :current="card.current" :previous="card.previous" :inverse="!!card.inverse" :formatter="card.format || formatNumber" />
            </div>
            <p class="text-sm text-gray-400 mt-2">{{ card.hint }}</p>
          </div>
        </div>
      </template>
    </div>

    <!-- Top Content — four ranked lists (Lives / Shows / Medias /
         Categories) ranked by total watch time. -->
    <div class="mb-6">
      <TopContentCard :loading="panelLoading.topContent" :data="data?.topContent || {}" />
    </div>

    <!-- Geographic Distribution — split card: world map on the
         left (session-sourced lat/lng, same data the Lives detail
         map uses), tabbed top-10 table on the right (Countries /
         Regions / Cities, ranked by validated streams). -->
    <div class="mb-6">
      <GeoDistributionCard
        :loading="panelLoading.geoDistribution || panelLoading.geographyMap"
        :data="data?.topGeo || {}"
        :geography="data?.geography || []"
      />
    </div>

    <!-- Demographics — age / gender / language. Same chart-canvas
         layout as the Lives detail view so the two pages read as one
         design. Bar chart for age (canonical bracket order), doughnut
         for gender, horizontal bar for language. -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
      <div class="bg-white rounded-xl border border-gray-200/60">
        <div class="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 class="text-[15px] font-semibold text-gray-900">Age Range</h2>
            <p class="text-sm text-gray-400 mt-0.5">Listener age distribution</p>
          </div>
          <span v-if="hasData('demographics.ageRange')" class="text-sm font-data font-medium text-[var(--color-primary)] bg-[var(--color-primary-light)] px-3 py-1 rounded">
            {{ formatNumber(chartTotal(data.demographics.ageRange)) }} total
          </span>
        </div>
        <div class="p-6">
          <LoadingBlock v-if="panelLoading.demographics" height="260" label="Loading chart..." />
          <template v-else-if="hasData('demographics.ageRange')">
            <div class="h-[260px] relative animate-fade-in"><canvas ref="ageRangeChart"></canvas></div>
          </template>
          <template v-else><EmptyChart label="No age data available" /></template>
        </div>
      </div>

      <div class="bg-white rounded-xl border border-gray-200/60">
        <div class="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 class="text-[15px] font-semibold text-gray-900">Gender</h2>
            <p class="text-sm text-gray-400 mt-0.5">Listener gender breakdown</p>
          </div>
          <span v-if="hasData('demographics.gender')" class="text-sm font-data font-medium text-[var(--color-primary)] bg-[var(--color-primary-light)] px-3 py-1 rounded">
            {{ formatNumber(chartTotal(data.demographics.gender)) }} total
          </span>
        </div>
        <div class="p-6">
          <LoadingBlock v-if="panelLoading.demographics" height="260" label="Loading chart..." />
          <template v-else-if="hasData('demographics.gender')">
            <div class="h-[260px] relative animate-fade-in"><canvas ref="genderChart"></canvas></div>
          </template>
          <template v-else><EmptyChart label="No gender data available" /></template>
        </div>
      </div>

      <div class="bg-white rounded-xl border border-gray-200/60">
        <div class="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 class="text-[15px] font-semibold text-gray-900">Language</h2>
            <p class="text-sm text-gray-400 mt-0.5">Preferred listener languages</p>
          </div>
          <span v-if="hasData('demographics.language')" class="text-sm font-data font-medium text-[var(--color-primary)] bg-[var(--color-primary-light)] px-3 py-1 rounded">
            {{ formatNumber(chartTotal(data.demographics.language)) }} total
          </span>
        </div>
        <div class="p-6">
          <LoadingBlock v-if="panelLoading.demographics" height="260" label="Loading chart..." />
          <template v-else-if="hasData('demographics.language')">
            <div class="h-[260px] relative animate-fade-in"><canvas ref="languageChart"></canvas></div>
          </template>
          <template v-else><EmptyChart label="No language data available" /></template>
        </div>
      </div>
    </div>

    <!-- Audience — devices / OS / browsers as compact donuts. -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
      <DonutCard title="Devices"          subtitle="Device type distribution" :loading="panelLoading.devices" chart-key="devices"  :data="data?.devices          || []" empty-label="No device data" />
      <DonutCard title="Operating Systems" subtitle="OS distribution"          :loading="panelLoading.devices" chart-key="os"       :data="data?.operatingSystems || []" empty-label="No OS data" />
      <DonutCard title="Browsers"         subtitle="Browser distribution"     :loading="panelLoading.devices" chart-key="browsers" :data="data?.browsers         || []" empty-label="No browser data" />
    </div>

    <!-- Acquisition + Distribution — referrers + embed hosts. -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
      <BreakdownCard title="Top Referrers"   subtitle="Where listeners came from" :loading="panelLoading.referrers" :rows="topRows('referrers')"  empty-label="No referrer data" />
      <BreakdownCard title="Top Embed Hosts" subtitle="Where the player runs"     :loading="panelLoading.referrers" :rows="topRows('embedHosts')" empty-label="No embed host data" />
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js/auto'
import { mapGetters } from 'vuex'
import api from '../../services/api.js'
import LoadingBlock from '../../components/LoadingBlock.vue'
import { notifyError } from '../../services/notifications.js'
import { buildTabReport } from './report-export.js'
import BreakdownCard from './components/BreakdownCard.vue'
import DonutCard from './components/DonutCard.vue'
import TopContentCard from './components/TopContentCard.vue'
import GeoDistributionCard from './components/GeoDistributionCard.vue'
import DeltaBadge from './components/DeltaBadge.vue'

// Same palette as lives.vue so demographic charts read identically
// across the two views.
const COLORS = {
  bar: 'rgba(51, 147, 255, 0.65)',
  doughnut: ['#3393ff', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#6b7280'],
  grid: '#f5f5f5',
  tick: '#a3a3a3'
}
const CHART_SCALES = {
  x: { grid: { display: false }, ticks: { font: { family: 'Poppins', size: 11 }, color: COLORS.tick } },
  y: { grid: { color: COLORS.grid }, ticks: { font: { family: 'JetBrains Mono', size: 11 }, color: COLORS.tick } }
}

const EmptyChart = {
  props: { label: String },
  template: `
    <div class="flex flex-col items-center justify-center py-12 text-center">
      <div class="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mb-3">
        <svg class="w-5 h-5 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
        </svg>
      </div>
      <p class="text-base text-gray-400">{{ label }}</p>
    </div>
  `
}

/**
 * Analytics Overview — cross-stream digest of org-wide activity over
 * the date range applied in the parent toolbar. No entity filters
 * (live / show / media) are exposed here; this view is always
 * unscoped. Watches `applied` from the analytics store so it
 * reloads whenever the user clicks Search in the parent toolbar.
 */
export default {
  name: 'AnalyticsOverview',
  components: { EmptyChart, LoadingBlock, BreakdownCard, DonutCard, TopContentCard, GeoDistributionCard, DeltaBadge },

  data() {
    return {
      loading: false,
      data: null,
      activeInfoCard: null,
      // Chart.js instances keyed by series — destroyed and recreated on
      // each data refresh to avoid stale renders / canvas leaks.
      charts: {},
      // Per-panel loading flags. Same shape as lives.vue so future
      // template surgery (LoadingBlock per section instead of the
      // top-level `loading` boolean) can be added without changing
      // the fetch machinery. Keys match panelTable() below.
      panelLoading: {
        kpis: false, topContent: false, geoDistribution: false, geographyMap: false,
        demographics: false, devices: false, referrers: false
      }
    }
  },

  computed: {
    ...mapGetters('analytics', ['applied']),

    /**
     * Six KPI cards driven by the new session model. Same shape as
     * lives.vue / medias.vue — Overview reads the org-wide slice of
     * the same `data.sessions` + `data.uniqueUsers` blocks.
     */
    kpiCards() {
      const d = this.data
      const s = d?.sessions || {}
      const u = d?.uniqueUsers || {}
      return [
        {
          key: 'uniqueUsers', label: 'Unique Users',
          value: this.formatNumber(u.total),
          current: u.total || 0, previous: u.totalPrev,
          hint: 'Distinct listeners in period',
          info: 'Distinct listeners in the period. One person opening the player three times counts as 1.',
          icon: '<svg class="w-4 h-4 text-rose-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>',
          iconBg: 'bg-rose-50'
        },
        {
          key: 'sessions', label: 'Sessions',
          value: this.formatNumber(s.total || 0),
          current: s.total || 0, previous: s.totalPrev,
          hint: 'Sessions in period',
          info: 'Count of sessions in the window. A session opens on the first play intent and closes on SDK destroy or a 30-minute continuous-pause timeout. A fresh play after an idle-timeout mints a new session.',
          icon: '<svg class="w-4 h-4 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="18" x2="12" y2="21"/></svg>',
          iconBg: 'bg-blue-50'
        },
        {
          key: 'totalSessionDuration', label: 'Total Session Duration',
          value: this.formatDurationMs(s.totalDurationMs),
          current: s.totalDurationMs || 0, previous: s.totalDurationMsPrev,
          format: this.formatDurationMs,
          hint: 'Wall-clock across sessions',
          info: 'Sum of session_duration across every session. Wall-clock = playing (content + ads) + paused. Includes idle-timeout sessions.',
          icon: '<svg class="w-4 h-4 text-indigo-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>',
          iconBg: 'bg-indigo-50'
        },
        {
          key: 'avgSessionDuration', label: 'Avg Session Duration',
          value: this.formatDurationMs(s.avgDurationMs),
          current: s.avgDurationMs || 0, previous: s.avgDurationMsPrev,
          format: this.formatDurationMs,
          hint: 'Total duration ÷ sessions',
          info: 'Average wall-clock time a session lasted. Formula: total_session_duration ÷ sessions.',
          icon: '<svg class="w-4 h-4 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>',
          iconBg: 'bg-green-50'
        },
        {
          key: 'totalWatchTime', label: 'Total Watch Time',
          value: this.formatDurationMs(s.totalWatchTimeMs),
          current: s.totalWatchTimeMs || 0, previous: s.totalWatchTimeMsPrev,
          format: this.formatDurationMs,
          hint: 'Content-only playing time',
          info: 'Sum of time the media element was playing content across every session. Excludes ads (tracked separately in the Ads section) and paused / buffering periods. This is "how long people listened to your content."',
          icon: '<svg class="w-4 h-4 text-violet-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>',
          iconBg: 'bg-violet-50'
        },
        {
          key: 'avgWatchTime', label: 'Avg Watch Time',
          value: this.formatDurationMs(s.avgWatchTimeMs),
          current: s.avgWatchTimeMs || 0, previous: s.avgWatchTimeMsPrev,
          format: this.formatDurationMs,
          hint: 'Content play per session',
          info: 'Average content-play time per session. Formula: total_watch_time ÷ sessions.',
          icon: '<svg class="w-4 h-4 text-amber-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>',
          iconBg: 'bg-amber-50'
        }
      ]
    }
  },

  watch: {
    applied: { handler() { this.loadOverview() }, deep: true }
  },

  mounted() {
    // Non-reactive instance state — same convention as lives.vue.
    //   _runId:      monotonic counter — bumped every loadOverview() call.
    //                In-flight fetches capture the value when queued;
    //                if it doesn't match by the time the response lands,
    //                the fetch drops silently (a newer filter set took over).
    //   _panelChain: promise chain that serializes every panel fetch so
    //                they hit Mongo one at a time, not all at once.
    this._runId = 0
    this._panelChain = Promise.resolve()
    // AbortController for every in-flight /analytics/* request. Aborted
    // on unmount / filter-change so a nav-away or re-search cancels the
    // previous run's HTTP work.
    this._abort = new AbortController()

    this.loadOverview()
    // Close info popover on outside click. Bound to document so
    // any click outside a card's `?`/popover dismisses it (the
    // popover itself stops propagation, the `?` toggles).
    this._onDocClick = () => { this.activeInfoCard = null }
    document.addEventListener('click', this._onDocClick)
  },

  beforeUnmount() {
    // Abort but keep the controller so queued `_fetchPanel` calls
    // inherit an already-aborted signal instead of `undefined`.
    if (this._abort) this._abort.abort()
    Object.values(this.charts).forEach(c => { if (c) c.destroy() })
    if (this._onDocClick) document.removeEventListener('click', this._onDocClick)
  },

  methods: {
    /**
     * Report for the Download button (PDF / CSV) — built from the data
     * this tab already loaded, so it matches the screen. Null while
     * nothing has loaded yet.
     */
    buildReport() {
      if (!this.data) return null
      const a = this.applied
      return buildTabReport({
        title: 'Overview Audience Report',
        kpiCards: this.kpiCards,
        data: this.data,
        applied: a,
        granularity: this.granularityFor(
          a.dateFrom ? new Date(a.dateFrom).toISOString() : null,
          a.dateTo ? new Date(a.dateTo).toISOString() : null,
          a.presetLabel
        )
      })
    },

    /**
     * Toggle the per-card info popover. Same key click-twice closes;
     * different key swaps to the new card; null closes all.
     */
    toggleInfo(key) {
      this.activeInfoCard = this.activeInfoCard === key ? null : key
    },

    /**
     * Fetch the overview payload for the currently-applied date range,
     * one panel at a time. Same serialized queue pattern as lives.vue:
     * each panel is a discrete /analytics/{panel}/overview request that
     * appends onto `_panelChain`, so DB pressure stays capped at one
     * query at a time. Panel results deep-merge into `this.data` as
     * they land, so the templates can pick them up progressively.
     *
     * Emits `loading` up to the parent (toolbar's Search spinner) at
     * both the start and after the last panel completes.
     */
    async loadOverview() {
      const params = {}
      if (this.applied.dateFrom) params.dateFrom = new Date(this.applied.dateFrom).toISOString()
      if (this.applied.dateTo)   params.dateTo   = new Date(this.applied.dateTo).toISOString()
      if (this.applied.appName)  params.appName  = this.applied.appName
      if (this.applied.categoryIds?.length) params.categoryId = this.applied.categoryIds.join(',')
      params.granularity = this.granularityFor(params.dateFrom, params.dateTo, this.applied.presetLabel)

      // ---- TEMP: isolation testing ----
      // Restrict the eager fetches to a specific subset while we verify
      // per-panel behaviour after the writer rewrite. Set to null to
      // load every panel.
      const ISOLATE_PANELS = null

      this.loading = true
      this.$emit('loading', true)
      this.data = null
      Object.values(this.charts).forEach(c => { if (c) c.destroy() })
      this.charts = {}

      const panels = this.panelTable('overview', params)
        .filter(p => !ISOLATE_PANELS || ISOLATE_PANELS.includes(p.key))

      // Only flip on the loading flags for panels we're actually going
      // to fetch — the rest stay false so any future v-if="panelLoading.*"
      // in the templates doesn't spin on nothing.
      const activeKeys = new Set(panels.map(p => p.key))
      for (const key of Object.keys(this.panelLoading)) {
        this.panelLoading[key] = activeKeys.has(key)
      }

      // Abort any in-flight requests from the previous run so the
      // network work is actually cancelled — not just its result
      // discarded downstream via the runId guard.
      if (this._abort) this._abort.abort()
      this._abort = new AbortController()

      // Bump the run id so any in-flight fetches from a superseded
      // loadOverview() (previous filter set) drop silently. Reset the
      // chain so new enqueues start a fresh promise chain.
      const runId = ++this._runId
      this._panelChain = Promise.resolve()

      let tail = Promise.resolve()
      for (const p of panels) tail = this._enqueue(() => this._fetchPanel(p, runId))

      try {
        await tail
      } finally {
        if (this._runId === runId) {
          this.loading = false
          this.$emit('loading', false)
          // Demographics canvases render via the demographics panel's
          // render callback when its data lands — no end-of-queue call
          // needed here.
        }
      }
    },

    /**
     * Table of every panel the view knows how to fetch. Callable so
     * render closures can capture the correct `this`. Kept in the
     * order the queue should hit them.
     */
    panelTable(scope, params) {
      // `geographyMap` is queued last on purpose — it runs a sampled
      // session scan (~3s) that other panels shouldn't wait behind.
      // The map pane inside GeoDistributionCard flips on as soon as
      // `data.geography` populates.
      return [
        { key: 'kpis',             url: 'kpis',             scope, params },
        { key: 'topContent',       url: 'top-content',      scope, params },
        { key: 'geoDistribution',  url: 'geo-distribution', scope, params },
        { key: 'demographics',     url: 'demographics',     scope, params, render: () => this.renderDemographicsCharts() },
        { key: 'devices',          url: 'devices',          scope, params },
        { key: 'referrers',        url: 'referrers',        scope, params },
        { key: 'geographyMap',     url: 'geography-map',    scope, params }
      ]
    },

    /**
     * Append a task onto the shared `_panelChain` so panel fetches run
     * one at a time in enqueue order. Errors are swallowed here so a
     * single failing panel doesn't derail the chain — `_fetchPanel`
     * surfaces the error via notifyError before rejecting.
     */
    _enqueue(task) {
      this._panelChain = (this._panelChain || Promise.resolve())
        .then(() => task().catch(() => {}))
      return this._panelChain
    },

    /**
     * Run one panel's fetch, drop the result if a newer run has
     * superseded us, and deep-merge the response into `this.data` so
     * per-namespace fields from different panels accumulate instead
     * of overwriting each other (same collision fix as lives.vue —
     * two panels can both write to `viewers` etc.).
     */
    async _fetchPanel(p, runId) {
      if (runId != null && this._runId !== runId) return
      const signal = this._abort ? this._abort.signal : undefined
      // Short-circuit if aborted — queued panels behind an in-flight
      // request shouldn't reach the network after unmount / re-run.
      if (signal && signal.aborted) return
      try {
        const res = await api.get(`/analytics/${p.url}/${p.scope}`, { params: p.params, silent: true, signal })
        if (runId != null && this._runId !== runId) return
        const incoming = res.data || {}
        const merged = { ...(this.data || {}) }
        for (const [k, v] of Object.entries(incoming)) {
          merged[k] = (v && typeof v === 'object' && !Array.isArray(v) && merged[k] && typeof merged[k] === 'object' && !Array.isArray(merged[k]))
            ? { ...merged[k], ...v }
            : v
        }
        this.data = merged
      } catch (err) {
        if (runId != null && this._runId !== runId) return
        // AbortController.abort() — expected during nav / filter change.
        if (err?.name === 'CanceledError' || err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return
        notifyError(err?.message || `Failed to load ${p.url}`)
      } finally {
        if (runId == null || this._runId === runId) {
          this.panelLoading[p.key] = false
          if (p.render) {
            await this.$nextTick()
            p.render()
          }
        }
      }
    },

    /**
     * Re-draw the three demographics charts. Called once per data
     * refresh; each renderer destroys its previous Chart.js instance
     * so stale canvases don't leak memory or paint zombie pixels.
     */
    renderDemographicsCharts() {
      this.renderAgeRangeChart()
      this.renderGenderChart()
      this.renderLanguageChart()
    },

    renderAgeRangeChart() {
      const ctx = this.$refs.ageRangeChart
      if (!ctx) return
      this.destroyChart('ageRange')
      const order = ['18+', '21+', '18-34', '18-49', '21-34', '18-24', '21-24', '25-34', '25-49', '25-54', '35-44', '45-54', '55-64', '55+', '65+']
      const vals = order.map(age => {
        const f = (this.data?.demographics?.ageRange || []).find(d => d._id === age)
        return f ? f.count : 0
      })
      this.charts.ageRange = new Chart(ctx, {
        type: 'bar',
        data: { labels: order, datasets: [{ data: vals, backgroundColor: COLORS.bar, borderRadius: 4 }] },
        options: {
          responsive: true,
          maintainAspectRatio: false, animation: false,
          plugins: { legend: { display: false } },
          scales: {
            ...CHART_SCALES,
            x: { ...CHART_SCALES.x, ticks: { ...CHART_SCALES.x.ticks, font: { family: 'Poppins', size: 10 } } }
          }
        }
      })
    },

    renderGenderChart() {
      const ctx = this.$refs.genderChart
      if (!ctx) return
      this.destroyChart('gender')
      const d = this.data?.demographics?.gender || []
      this.charts.gender = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: d.map(x => x._id),
          datasets: [{ data: d.map(x => x.count), backgroundColor: COLORS.doughnut.slice(0, d.length) }]
        },
        options: {
          responsive: true, maintainAspectRatio: false, animation: false, cutout: '65%',
          plugins: { legend: { position: 'bottom', labels: { font: { family: 'Poppins', size: 12 }, padding: 16, usePointStyle: true, pointStyle: 'circle' } } }
        }
      })
    },

    renderLanguageChart() {
      const ctx = this.$refs.languageChart
      if (!ctx) return
      this.destroyChart('language')
      // Map keyed in lowercase; lowercase the key on lookup so the
      // post-normalization uppercase codes ("ES", "EN") still resolve
      // without needing duplicate entries.
      const langNames = { en: 'English', es: 'Spanish', fr: 'French', de: 'German', pt: 'Portuguese', it: 'Italian', ja: 'Japanese', zh: 'Chinese', ko: 'Korean', ar: 'Arabic', hi: 'Hindi', ru: 'Russian' }
      const d = this.data?.demographics?.language || []
      this.charts.language = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: d.map(x => langNames[String(x._id || '').toLowerCase()] || x._id),
          datasets: [{ data: d.map(x => x.count), backgroundColor: COLORS.bar, borderRadius: 4 }]
        },
        options: {
          indexAxis: 'y', responsive: true, maintainAspectRatio: false, animation: false, plugins: { legend: { display: false } },
          scales: {
            x: { grid: { color: COLORS.grid }, ticks: { font: { family: 'JetBrains Mono', size: 10 }, color: COLORS.tick } },
            y: { grid: { display: false }, ticks: { font: { family: 'Poppins', size: 11 }, color: '#737373' } }
          }
        }
      })
    },

    destroyChart(key) {
      const c = this.charts[key]
      if (c) { c.destroy(); this.charts[key] = null }
    },

    /**
     * Truthy-check on a nested data path. Used to gate chart rendering
     * + the "X total" header badge so we don't try to render an empty
     * canvas on an empty array.
     */
    hasData(path) {
      const parts = path.split('.')
      let v = this.data
      for (const p of parts) v = v?.[p]
      return Array.isArray(v) && v.length > 0
    },

    /**
     * Sum a numeric field across an array. Defaults to summing
     * `count`. Used for the "X total" badge in each chart header.
     */
    chartTotal(arr, key = 'count') {
      if (!Array.isArray(arr)) return 0
      return arr.reduce((s, x) => s + (Number(x?.[key]) || 0), 0)
    },

    /**
     * Map the active preset (or the duration for Custom) to the rollup
     * granularity the API should serve us. Same logic as on the Lives
     * tab so chart density looks consistent across views.
     */
    granularityFor(from, to, presetLabel) {
      const presetMap = {
        'Last Hour':     'minute',
        'Last 6 Hours':  'hour',
        'Today':         'hour',
        'Last 24 Hours': 'hour',
        'Yesterday':     'hour',
        'Last 7 Days':   'day',
        'Last 30 Days':  'day',
        'This Month':    'day',
        'Last Month':    'day',
        'Last Year':     'month'
      }
      if (presetMap[presetLabel]) return presetMap[presetLabel]
      if (!from || !to) return 'hour'
      const ms = new Date(to) - new Date(from)
      const hours = ms / 3600000
      const days  = hours / 24
      if (hours < 1)   return 'minute'
      if (hours < 24)  return 'hour'
      if (days  < 31)  return 'day'
      if (days  < 365) return 'month'
      return 'year'
    },

    topRows(path) {
      const parts = path.split('.')
      let arr = this.data
      for (const p of parts) arr = arr?.[p]
      if (!Array.isArray(arr)) return []
      return arr.slice(0, 10).map(r => ({
        id: r._id,
        label: r.title || r._id || 'Unknown',
        count: r.count || 0
      }))
    },

    formatNumber(n) {
      if (n == null || isNaN(n)) return '—'
      if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M'
      if (n >= 1_000)     return (n / 1_000).toFixed(1) + 'K'
      return Number(n).toLocaleString()
    },
    formatDurationMs(ms) {
      if (ms == null || isNaN(ms)) return '—'
      const totalSeconds = Math.round(ms / 1000)
      const h = Math.floor(totalSeconds / 3600)
      const m = Math.floor((totalSeconds % 3600) / 60)
      const s = totalSeconds % 60
      if (h > 0) return `${h}h ${m}m`
      if (m > 0) return `${m}m ${s}s`
      return `${s}s`
    }
  }
}
</script>
